/**
 * Shared timing vocabulary for the Bastian Keller sections.
 *
 * Sequences here are stacked, not chained: every step starts while the one before
 * it is still settling, so a reveal reads as one continuous gesture instead of a
 * queue of separate ones. Nothing waits for a predecessor to finish.
 */

/** Expo-ish ease-out — most of the distance is covered in the first third. */
export const EASE_OUT = [0.22, 1, 0.36, 1] as const;

/** How far through a step the next one begins. */
export const OVERLAP = 0.6;

/**
 * Where an EASE_OUT move is perceptually over: 95% of its distance is covered by
 * t=0.47, and the remaining 5% takes as long again. Chaining against the nominal
 * duration therefore leaves a visible dead gap — a 0.8s move looks finished at
 * 0.37s and spends its last 426ms doing nothing the eye can see.
 *
 * Use this only for a genuine hand-off, where one thing really must be mostly
 * done before the next makes sense. For a run of siblings, use STACK_STEP.
 */
export const EASE_OUT_SETTLED = 0.47;

/**
 * Gap between siblings in a cascade — small, fixed, and independent of how long
 * the move itself runs.
 *
 * This is the difference between stacked and merely sequential. At 90ms against a
 * ~700ms rise, six elements are mid-flight at any moment and the group reads as
 * one gesture. Deriving the gap from the duration instead — even from the
 * perceptual settle point above — spaces siblings out by hundreds of ms and each
 * one visibly waits its turn, however much they technically overlap.
 */
export const STACK_STEP = 0.09;

/**
 * Start time for a step following a `duration`-long move that began at `start`.
 *
 * Handing over at 0.6 is earlier than it sounds: EASE_OUT is front-loaded enough
 * that the outgoing move has already covered ~90% of its distance by then, so the
 * two overlap smoothly rather than colliding. Pass a higher `overlap` for a step
 * that genuinely needs more of its predecessor on screen first.
 */
export const after = (
  start: number,
  duration: number,
  overlap: number = OVERLAP,
) => Number((start + duration * overlap).toFixed(3));

/** Scroll-linked drift, damped well past oscillation so it only ever trails. */
export const PARALLAX_SPRING = {
  stiffness: 100,
  damping: 28,
  mass: 0.55,
  restDelta: 0.0005,
} as const;
